
import { ICefSharp } from "./Interfaces/_ICefSharp";
import { IConfigRepository } from "../../../interfaces/IConfigRepository";
import { AppConfigurationData } from "../../../models/AppConfigurationData";
import { ICefSharpAppConfigurationService } from "./Interfaces/ICefSharpAppConfigurationService";
import AuthService from "Infrastructure/repositories/auth/AuthService";
import { AcquirerType } from "Infrastructure/repositories/auth/IAuthService";
import { getDeviceInfo } from "modules/reactNativeBridge/ReactNativeBridge";
import { isReactNative } from "utils/constants";
import { HostConfig } from "./toHostConfig";

declare const CefSharp: ICefSharp;
declare const appConfigurationService: ICefSharpAppConfigurationService;

let cachedConfig: AppConfigurationData | null = null;
let pendingRequest: Promise<AppConfigurationData> | null = null;

const HOST_OBJECT_NAME = "appConfigurationService";

// Windows: a casca informa MAC e versão; o login acontece aqui, como no Android.
const loadCefSharpConfigAsync = async (): Promise<AppConfigurationData> => {
  await CefSharp.BindObjectAsync(HOST_OBJECT_NAME);
  const host = await appConfigurationService.get();

  // Casca antiga: o site de configuração já fez o login e entregou tudo pronto.
  if (host.Token && host.EquipamentId) {
    return {
      printType: host.PrintType,
      ticketNote: host.TicketNote,
      tickectName: host.TickectName,
      printQRCodeTicket: host.PrintQRCodeTicket,
      cnpj: host.CNPJ,
      userAgent: host.UserAgent ?? "",
      urlApp: host.UrlApp,
      equipamentId: host.EquipamentId,
      token: host.Token,
      macAddress: host.MacAddress,
      cefSharpVersion: host.VersaoCefSharp,
    };
  }

  if (!host.MacAddress) {
    throw new Error("A casca não informou o endereço MAC do equipamento.");
  }

  const authResponse = await AuthService().authenticate(host.MacAddress, host.MacAddress);

  return {
    printType: 0,
    ticketNote: "",
    tickectName: "",
    printQRCodeTicket: false,
    cnpj: "",
    userAgent: authResponse.name,
    urlApp: "",
    equipamentId: authResponse.id,
    token: authResponse.token,
    macAddress: host.MacAddress,
    cefSharpVersion: host.VersaoCefSharp,
  };
};

const getCefSharpConfigAsync = (): Promise<AppConfigurationData> => {
  if (cachedConfig) {
    return Promise.resolve(cachedConfig);
  }

  if (!pendingRequest) {
    pendingRequest = loadCefSharpConfigAsync()
      .then((config) => {
        cachedConfig = config;
        return config;
      })
      .finally(() => {
        pendingRequest = null;
      });
  }

  return pendingRequest;
};

export const CefSharpConfigRepository = (): IConfigRepository => {

  const getConfigAsync = async (): Promise<AppConfigurationData> => {

    // Sem host CefSharp (Android): cai no fluxo de device info do bridge nativo.
    if (isReactNative) {

      const authService = AuthService();


      const deviceInfo = await getDeviceInfo();

      if(deviceInfo) {
        const authResponse = await authService.authenticate(deviceInfo.serialNumber, deviceInfo.serialNumber, AcquirerType.PAGSEGURO);

      return {
        printType: 0,
        ticketNote: "",
        tickectName: "",
        printQRCodeTicket: false,
        cnpj: "",
        userAgent: "",
        urlApp: "",
        equipamentId: authResponse.id,
        token: authResponse.token,
        macAddress: deviceInfo.serialNumber,
      }
      } else {
        throw new Error("Device info not found");
     }
    }

    return getCefSharpConfigAsync();
  };

  // Devolve à casca os dados que os serviços C# (impressão, TEF) leem.
  const updateHostAsync = async (config: HostConfig): Promise<void> => {
    await CefSharp.BindObjectAsync(HOST_OBJECT_NAME);

    if (typeof appConfigurationService.update !== "function") {
      return;
    }

    await appConfigurationService.update(JSON.stringify(config));
  };

  // Identificador do aparelho (MAC no Windows), direto da casca e sem depender da API:
  // é com ele que um equipamento novo é cadastrado.
  const getDeviceIdAsync = async (): Promise<string> => {
    await CefSharp.BindObjectAsync(HOST_OBJECT_NAME);
    const host = await appConfigurationService.get();

    return host.MacAddress ?? "";
  };

  return { getConfigAsync, updateHostAsync, getDeviceIdAsync };
};
